import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Quote, ChevronRight, Loader2 } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { fetchCaseStudyBySlug, fetchCaseStudyList } from "@/lib/cms";

const SITE_BASE = typeof window !== "undefined" ? window.location.origin : "";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const [cs, setCs] = useState(undefined);
  const [more, setMore] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetchCaseStudyBySlug(slug)
      .then((c) => { if (mounted) setCs(c || null); })
      .catch(() => { if (mounted) setCs(null); });
    fetchCaseStudyList()
      .then(({ items }) => { if (mounted) setMore(items.filter((c) => c.slug !== slug).slice(0, 3)); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [slug]);

  const ld = useMemo(() => {
    if (!cs) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${SITE_BASE}/case-studies/${cs.slug}`,
      headline: cs.headline,
      description: cs.summary,
      image: [cs.cover],
      author: { "@type": "Organization", name: "CoSec Global" },
      publisher: { "@type": "Organization", name: "CoSec Global" },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_BASE}/case-studies/${cs.slug}` },
      about: { "@type": "Organization", name: cs.company },
    };
  }, [cs]);

  if (cs === undefined) {
    return (
      <SiteLayout>
        <div className="container-cv py-24 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading case study…
        </div>
      </SiteLayout>
    );
  }
  if (cs === null) return <Navigate to="/case-studies" replace />;

  return (
    <SiteLayout
      title={cs.headline}
      description={cs.summary}
      image={cs.cover}
      type="article"
      jsonLd={ld}
      breadcrumbs={[
        { name: "Home", href: `${SITE_BASE}/` },
        { name: "Case studies", href: `${SITE_BASE}/case-studies` },
        { name: cs.company, href: `${SITE_BASE}/case-studies/${cs.slug}` },
      ]}
    >

      <div className="border-b border-border bg-secondary/40">
        <div className="container-cv py-3 flex items-center gap-1 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/case-studies" className="hover:text-primary">Case studies</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-primary font-medium truncate max-w-[60ch]">{cs.company}</span>
        </div>
      </div>

      <section className="py-12 md:py-20">
        <div className="container-cv grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Link to="/case-studies" data-testid="case-back" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:text-accent transition-colors">
              <ArrowLeft className="h-4 w-4" /> All case studies
            </Link>
            <div className="mt-5 flex items-center gap-3 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-accent/15 text-primary font-bold uppercase tracking-wider">{cs.sector}</span>
              <span className="text-muted-foreground">{cs.region}</span>
            </div>
            <div className="mt-3 font-display text-xs font-bold uppercase tracking-[0.18em] text-accent">{cs.company}</div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-primary leading-[1.06] tracking-tight">
              {cs.headline}
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">{cs.summary}</p>

            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {(cs.metrics || []).map((m, i) => (
                <div key={i} className="rounded-xl border border-border bg-white p-3 text-center">
                  <div className="font-display text-2xl font-bold text-primary leading-tight">{m.k}</div>
                  <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{m.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-border aspect-[4/5] shadow-[0_30px_60px_-30px_rgba(14,58,95,0.35)]">
              <img src={cs.cover} alt={cs.company} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-white/95 backdrop-blur p-4">
                <Quote className="h-5 w-5 text-accent" />
                <p className="mt-2 text-sm text-primary/90 leading-relaxed">&ldquo;{cs.quote?.q}&rdquo;</p>
                <div className="mt-3 text-xs">
                  <div className="font-display font-bold text-primary">{cs.quote?.n}</div>
                  <div className="text-muted-foreground">{cs.quote?.r}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-cv max-w-3xl space-y-10">
          {(cs.body || []).map((b, i) => (
            <div key={i}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">{b.h}</h2>
              {(b.p || []).map((para, j) => (
                <p key={j} className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">{para}</p>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
        <div className="container-cv relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.04]">
              See how CoSec App can change your{" "}
              <span className="text-accent">compliance cycle.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
              <Link to="/contact">Book a Demo <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-primary font-bold">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </section>

      {more.length > 0 && (
        <section className="py-16 md:py-20 bg-secondary/40 border-t border-border">
          <div className="container-cv">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">More customer stories</h2>
              <Link to="/case-studies" className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent transition-colors">
                All case studies <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {more.map((c) => (
                <Link key={c.slug} to={`/case-studies/${c.slug}`} className="group rounded-2xl border border-border bg-white overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={c.cover} alt={c.company} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-accent">{c.company}</div>
                    <h3 className="mt-2 font-display text-base font-bold text-primary leading-snug group-hover:text-accent transition-colors">{c.headline}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
}
